import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Box,
  Text,
  Button,
  HStack,
  Badge,
  useColorModeValue,
  Divider,
  AspectRatio,
  Stack,
} from "@chakra-ui/react";
import { openUrl } from "../../../_Utils/mobileBridge";

const escapeHtml = (s = "") =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const autoLink = (s = "") =>
  s.replace(
    /((https?:\/\/)[^\s<>"]+)/g,
    '<a href="$1" target="_blank" rel="noreferrer">$1</a>'
  );

const toRichHTML = (raw = "") => {
  if (!raw) return "";
  const looksLikeHTML = /<\/?[a-z][\s\S]*>/i.test(raw);
  if (looksLikeHTML) return autoLink(raw);

  if (/\b1\)\s/.test(raw)) {
    const items = raw
      .split(/\b\d+\)\s*/g)
      .map((s) => s.trim())
      .filter(Boolean);
    if (items.length > 1) {
      return `<ol style="margin-left:1.25rem; padding-left:1rem;">
        ${items.map((it) => `<li>${autoLink(escapeHtml(it))}</li>`).join("")}
      </ol>`;
    }
  }

  const lines = raw.split(/\r?\n/);
  const bulletLines = lines.filter((l) => /^(\s*[-•]\s+)/.test(l));
  if (bulletLines.length >= 2) {
    const items = lines
      .map((l) => l.replace(/^(\s*[-•]\s+)/, "").trim())
      .filter(Boolean);
    return `<ul style="margin-left:1.25rem; padding-left:1rem;">
      ${items.map((it) => `<li>${autoLink(escapeHtml(it))}</li>`).join("")}
    </ul>`;
  }

  return autoLink(escapeHtml(raw).replace(/\n/g, "<br/>"));
};

const toEmbedSrc = (url = "") => {
  if (!url) return "";
  if (/youtu\.?be/.test(url)) {
    if (/youtube\.com\/shorts\//.test(url))
      return url.replace("youtube.com/shorts/", "www.youtube.com/embed/");
    if (/watch\?v=/.test(url)) return url.replace("watch?v=", "embed/");
    if (/youtu\.be\//.test(url))
      return url.replace("youtu.be/", "www.youtube.com/embed/");
    return url;
  }
  if (/vimeo\.com\/\d+/.test(url))
    return url.replace("vimeo.com/", "player.vimeo.com/video/");
  return url;
};

const parseMessage = (raw = "") => {
  const lines = (raw || "").split(/\r?\n/);
  let videoSpec = null;
  const remain = [];

  for (const line of lines) {
    const m = line.match(
      /^\s*(youtube_url|vimeo_url|video_url)\s*:\s*(\S+)\s*$/i
    );
    if (m) {
      const kindKey = m[1].toLowerCase();
      const url = m[2];
      if (kindKey === "youtube_url")
        videoSpec = { kind: "youtube", src: toEmbedSrc(url) };
      else if (kindKey === "vimeo_url")
        videoSpec = { kind: "vimeo", src: toEmbedSrc(url) };
      else videoSpec = { kind: "file", src: url };
      continue;
    }
    remain.push(line);
  }

  return { videoSpec, content: remain.join("\n") };
};

const authHeaders = () => {
  const token =
    localStorage.getItem("USER_AUTH") ||
    sessionStorage.getItem("USER_AUTH");
  return token ? { Authorization: `Bearer ${token}` } : undefined;
};

// onResuelto(hayAviso): avisa si hay algo para mostrar, para que otro modal
// de ingreso (Novedades de WAM) no se abra encima.
const BroadcastModal = ({ onResuelto } = {}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const onResueltoRef = useRef(onResuelto);
  onResueltoRef.current = onResuelto;
  const [notification, setNotification] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const bg = useColorModeValue("white", "gray.800");
  const headerColor = useColorModeValue("gray.900", "gray.100");
  const textColor = useColorModeValue("gray.700", "gray.300");
  const subtle = useColorModeValue("gray.400", "gray.500");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.200");

  const colorScheme = useMemo(() => {
    const sev = String(notification?.severity || "info").toLowerCase();
    if (sev === "warning") return "yellow";
    if (sev === "critical") return "red";
    if (sev === "promo") return "purple";
    return "blue";
  }, [notification]);

  const fetchNotification = useCallback(async () => {
    try {
      const response = await axios.get("/notificaciones/active/", {
        params: { display_type: "modal" },
        headers: authHeaders(),
      });
      if (response.status === 200 && response.data) {
        setNotification(response.data);
        onResueltoRef.current?.(true);
      } else {
        setNotification(null);
        onResueltoRef.current?.(false);
      }
    } catch (error) {
      onResueltoRef.current?.(false);
      if (error?.response?.status === 204) {
        setNotification(null);
        return;
      }
      console.error("Error al obtener notificaciones:", error);
      setNotification(null);
    }
  }, []);

  useEffect(() => {
    fetchNotification();
  }, [fetchNotification]);

  useEffect(() => {
    if (notification) onOpen();
    else onClose();
  }, [notification, onOpen, onClose]);

  const postAction = useCallback(async (id, action) => {
    try {
      await axios.post(`/notificaciones/${id}/${action}/`, null, {
        headers: authHeaders(),
      });
    } catch (error) {
      console.error(`Error al registrar ${action}:`, error);
    }
  }, []);

  useEffect(() => {
    if (notification?.id) {
      postAction(notification.id, "seen");
    }
  }, [notification?.id, postAction]);

  const resetAndFetch = useCallback(() => {
    setNotification(null);
    setTimeout(() => {
      fetchNotification();
    }, 400);
  }, [fetchNotification]);

  const handleDismiss = useCallback(async () => {
    if (!notification || (!notification.dismissible && notification.require_ack))
      return;
    setActionLoading(true);
    await postAction(notification.id, "dismiss");
    setActionLoading(false);
    resetAndFetch();
  }, [notification, postAction, resetAndFetch]);

  const handleAck = useCallback(async () => {
    if (!notification) return;
    setActionLoading(true);
    await postAction(notification.id, "ack");
    setActionLoading(false);
    resetAndFetch();
  }, [notification, postAction, resetAndFetch]);

  const handleModalClose = useCallback(() => {
    if (notification?.require_ack && !notification?.dismissible) return;
    handleDismiss();
  }, [notification, handleDismiss]);

  const handleCTAClick = useCallback(
    (url, requiresAck) => {
      if (!url) return;
      openUrl(url);
      if (requiresAck) {
        handleAck();
      }
    },
    [handleAck]
  );

  const hasPrimaryCTA = Boolean(notification?.cta_text && notification?.cta_url);
  const hasSecondaryCTA = Boolean(
    notification?.secondary_cta_text && notification?.secondary_cta_url
  );

  const showCloseButton =
    notification?.dismissible &&
    !notification?.require_ack;

  const { videoSpec, content } = parseMessage(
    notification?.mensaje || notification?.texto || ""
  );

  return (
    <Modal
      isOpen={Boolean(isOpen && notification)}
      onClose={handleModalClose}
      isCentered
      motionPreset="scale"
      closeOnOverlayClick={!notification?.require_ack}
    >
      <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(2px)" />
      <ModalContent
        bg={bg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="xl"
        shadow="xl"
        maxW="640px"
      >
        <ModalHeader
          color={headerColor}
          fontSize="lg"
          fontWeight="700"
          lineHeight="1.2"
          pb={1}
        >
          <HStack spacing={3} align="center">
            <Text as="span">{notification?.titulo || "Aviso"}</Text>
            <Badge colorScheme={colorScheme} variant="subtle" borderRadius="md">
              {String(notification?.severity || "info").toUpperCase()}
            </Badge>
          </HStack>
          {notification?.fecha_creacion && (
            <Text fontSize="xs" color={subtle} mt={2} fontWeight="500">
              {new Date(notification.fecha_creacion).toLocaleString()}
            </Text>
          )}
        </ModalHeader>
        {showCloseButton && <ModalCloseButton />}
        <Divider mb={2} />
        <ModalBody color={textColor}>
          {notification?.image_url && (
            <Box
              mb={4}
              borderRadius="md"
              overflow="hidden"
              borderWidth="1px"
              borderColor={borderColor}
            >
              <Box
                as="img"
                src={notification.image_url}
                alt={notification.titulo || "Notificación"}
                w="100%"
              />
            </Box>
          )}
          {videoSpec ? (
            <AspectRatio ratio={16 / 9} mb={4}>
              {videoSpec.kind === "file" ? (
                <video controls src={videoSpec.src} />
              ) : (
                <iframe
                  title="video"
                  src={videoSpec.src}
                  allowFullScreen
                  style={{ border: "none" }}
                />
              )}
            </AspectRatio>
          ) : null}
          <Box
            fontSize="sm"
            lineHeight="1.6"
            dangerouslySetInnerHTML={{ __html: toRichHTML(content) }}
          />
        </ModalBody>
        <ModalFooter>
          <Stack
            direction={{ base: "column", sm: "row" }}
            spacing={3}
            w="100%"
            justify="flex-end"
          >
            {hasSecondaryCTA && (
              <Button
                variant="ghost"
                onClick={() =>
                  handleCTAClick(
                    notification.secondary_cta_url,
                    notification.require_ack
                  )
                }
                isDisabled={actionLoading}
              >
                {notification.secondary_cta_text}
              </Button>
            )}
            {hasPrimaryCTA && (
              <Button
                colorScheme={colorScheme}
                onClick={() =>
                  handleCTAClick(
                    notification.cta_url,
                    notification.require_ack
                  )
                }
                isDisabled={actionLoading}
              >
                {notification.cta_text}
              </Button>
            )}
            {notification?.require_ack ? (
              <Button
                colorScheme="blue"
                onClick={handleAck}
                isLoading={actionLoading}
              >
                Entendido
              </Button>
            ) : showCloseButton ? (
              <Button
                variant="outline"
                onClick={handleDismiss}
                isLoading={actionLoading}
              >
                Cerrar
              </Button>
            ) : null}
          </Stack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default BroadcastModal;
